import { createAction } from "@reduxjs/toolkit";
import type { SparxListResponse } from "@exagens/behavioral-insight-console-client";
import type { InteractionBlueprintData } from "@/lib/contentParsers/interactionBlueprintSchema";
import type { EditorPanelSlug } from "@/features/sparxsEditor/lib/editorUrl";
// --- Active file / selection ---

export type ActiveSparxSelectionResolvedPayload = {
  activeSparxFileName: string;
  clearSelectedInputField: boolean;
  persistDirtySession?: {
    fileName: string;
    data: InteractionBlueprintData;
  };
};

export const activeSparxSelectionResolved =
  createAction<ActiveSparxSelectionResolvedPayload>(
    "sparxsEditor/activeSparxSelectionResolved",
  );

export const sparxsEditorCatalogReplaced = createAction<SparxListResponse>(
  "sparxsEditor/catalogReplaced",
);

// --- Blueprint / session / save / conflict ---

export type SparxsEditorBlueprintFetchedHydratedPayload = {
  fileName: string;
  blueprint: InteractionBlueprintData;
  lastKnownServerModified?: number;
};

export const sparxsEditorBlueprintFetchedHydrated =
  createAction<SparxsEditorBlueprintFetchedHydratedPayload>(
    "sparxsEditor/blueprintFetchedHydrated",
  );

export type SparxSavedAfterServerOk = {
  fileName: string;
  blueprint: InteractionBlueprintData;
  serverLastModifiedMs: number;
};

export type SparxsEditorSaveSucceededForFilesPayload = {
  fileNames: string[];
  saved: SparxSavedAfterServerOk[];
  saveFeedbackId?: string;
};

export const sparxsEditorSaveSucceededForFiles =
  createAction<SparxsEditorSaveSucceededForFilesPayload>(
    "sparxsEditor/saveSucceededForFiles",
  );

export type SparxsEditorSaveRequestedPayload = {
  fileNames: string[];
  message: string;
  requestId?: string;
  saveFeedbackId?: string;
};

export const sparxsEditorSaveRequested =
  createAction<SparxsEditorSaveRequestedPayload>("sparxsEditor/saveRequested");

export type SparxsEditorSaveFailedPayload = {
  fileNames: string[];
  error: string;
  requestId?: string;
  saveFeedbackId?: string;
};

export const sparxsEditorSaveFailed =
  createAction<SparxsEditorSaveFailedPayload>("sparxsEditor/saveFailed");

export type SparxsEditorConflictResolvedWithServerBlueprintPayload = {
  fileName: string;
  activeFileName: string | null;
  blueprint: InteractionBlueprintData;
  lastKnownServerModified?: number;
};

export const sparxsEditorConflictResolvedWithServerBlueprint =
  createAction<SparxsEditorConflictResolvedWithServerBlueprintPayload>(
    "sparxsEditor/conflictResolvedWithServerBlueprint",
  );

export type SparxsEditorRevertedSessionFileToOriginalPayload = {
  fileName: string;
  snapshot: InteractionBlueprintData;
};

export const sparxsEditorRevertedSessionFileToOriginal =
  createAction<SparxsEditorRevertedSessionFileToOriginalPayload>(
    "sparxsEditor/revertedSessionFileToOriginal",
  );

/** User selected an editor panel tab (preferred panel + clear selected field). */
export type SparxsEditorPanelTabSelectedPayload = {
  preferredPanelSlug: EditorPanelSlug;
};

export const sparxsEditorPanelTabSelected =
  createAction<SparxsEditorPanelTabSelectedPayload>(
    "sparxsEditor/panelTabSelected",
  );
